/**
 * Global error handler for ResizeObserver and other common errors
 * This module provides comprehensive error handling for the application
 */

import React from 'react';

/**
 * Initialize global error handlers for ResizeObserver errors
 * Call this function early in your app initialization
 */
export const initializeErrorHandlers = () => {
  // Store original console methods
  const originalError = console.error;
  const originalWarn = console.warn;

  // console.log is opt-in. The codebase has hundreds of leftover debug
  // logs, which buried real warnings and errors. Turn them back on with
  // localStorage.setItem('tuneswell:debug', '1') and reload.
  let debugLogging = false;
  try {
    debugLogging = localStorage.getItem('tuneswell:debug') === '1';
  } catch (e) {
    // Storage unavailable (private mode etc.) - keep logs off
  }
  if (!debugLogging) {
    console.log = () => {};
  }

  // Override console.error to suppress ResizeObserver errors
  console.error = (...args) => {
    if (args[0] && typeof args[0] === 'string' && 
        (args[0].includes('ResizeObserver loop completed with undelivered notifications') ||
         args[0].includes('ResizeObserver loop limit exceeded'))) {
      return;
    }
    originalError.apply(console, args);
  };

  // Override console.warn to suppress ResizeObserver warnings
  console.warn = (...args) => {
    if (args[0] && typeof args[0] === 'string' && 
        (args[0].includes('ResizeObserver loop completed with undelivered notifications') ||
         args[0].includes('ResizeObserver loop limit exceeded'))) {
      return;
    }
    originalWarn.apply(console, args);
  };

  // Handle uncaught errors. Registered in the CAPTURE phase and calling
  // stopImmediatePropagation (not just preventDefault) because CRA's dev
  // error overlay (react-error-overlay) has its own 'error' listener -
  // preventDefault alone doesn't stop that other listener from firing on
  // the same event, which is why this harmless browser message was still
  // showing up as a blocking red screen despite this handler existing.
  const isResizeObserverLoopError = (message) =>
    typeof message === 'string' &&
    (message.includes('ResizeObserver loop completed with undelivered notifications') ||
     message.includes('ResizeObserver loop limit exceeded'));

  window.addEventListener('error', (event) => {
    if (isResizeObserverLoopError(event.message)) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return false;
    }
  }, true);

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && isResizeObserverLoopError(event.reason.message)) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return false;
    }
  }, true);
};

/**
 * Safe wrapper for ResizeObserver operations
 * @param {Function} callback - The callback to execute
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {Function} - Debounced and error-safe callback
 */
export const createSafeCallback = (callback, delay = 16) => {
  let timeoutId;
  
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      try {
        callback(...args);
      } catch (error) {
        // Silently handle ResizeObserver errors
        if (!error.message?.includes('ResizeObserver')) {
          console.error('Callback error:', error);
        }
      }
    }, delay);
  };
};

/**
 * Error boundary component for catching ResizeObserver errors
 */
export class ResizeObserverErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Don't show error boundary for ResizeObserver errors
    if (error.message && error.message.includes('ResizeObserver')) {
      return { hasError: false };
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Only log non-ResizeObserver errors
    if (!error.message || !error.message.includes('ResizeObserver')) {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <div>Something went wrong.</div>;
    }

    return this.props.children;
  }
}

